import React, { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView, Decoration, ViewPlugin, ViewUpdate, WidgetType } from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

class PillWidget extends WidgetType {
  constructor(private text: string) { super(); }
  toDOM() {
    const span = document.createElement("span");
    span.className = "cm-kgnf-pill";
    span.textContent = this.text;
    return span;
  }
  ignoreEvent() { return false; }
}

function makePillHighlighter(needles: string[]) {
  const terms = needles.filter(Boolean).slice().sort((a, b) => b.length - a.length);
  const re = terms.length ? new RegExp(terms.map(escapeRegex).join("|"), "gi") : null;

  return ViewPlugin.fromClass(
    class {
      decorations;
      constructor(view: EditorView) {
        this.decorations = this.build(view);
      }
      update(u: ViewUpdate) {
        if (u.docChanged || u.viewportChanged) {
          this.decorations = this.build(u.view);
        }
      }
      build(view: EditorView) {
        if (!re) return Decoration.none;

        const b = new RangeSetBuilder<Decoration>();
        for (const { from, to } of view.visibleRanges) {
          const text = view.state.doc.sliceString(from, to);
          re.lastIndex = 0;

          let m: RegExpExecArray | null;
          while ((m = re.exec(text))) {
            const a = from + m.index;
            const z = a + m[0].length;
            if (z <= a) continue;

            // Replace matched text with a widget that *looks* like a pill.
            b.add(a, z, Decoration.replace({ widget: new PillWidget(m[0]), inclusive: false }));
          }
        }
        return b.finish();
      }
    },
    { decorations: (v) => v.decorations }
  );
}

type Props = {
  value: string;
  onChange: (v: string) => void;
  highlights: string[];
};

export function HighlightedMultiline({ value, onChange, highlights }: Props) {
  const highlightsKey = useMemo(
    () => highlights.filter(Boolean).slice().sort().join("\u0001"),
    [highlights]
  );

  const pillPlugin = useMemo(() => makePillHighlighter(highlights), [highlightsKey]);

  const extensions = useMemo(
    () => [
      EditorView.lineWrapping,
      pillPlugin,
      // Font + sizing
      EditorView.theme({
        "&": {
          fontFamily: `"Segoe UI", system-ui, -apple-system, sans-serif`,
          fontSize: "14px",
        },
        ".cm-scroller": { fontFamily: "inherit" },
        ".cm-content": { padding: "8px 12px", fontFamily: "inherit" },

        // Pill look
        ".cm-kgnf-pill": {
          display: "inline-block",
          padding: "1px 8px",
          borderRadius: "999px",
          fontSize: "0.92em",
          lineHeight: "1.4",
          verticalAlign: "baseline",
          // pick your colors; these are just defaults
          backgroundColor: "rgba(0, 120, 212, 0.18)",
          border: "1px solid rgba(0, 120, 212, 0.35)",
        },
      }),
    ],
    [pillPlugin]
  );

  return (
    <CodeMirror
      value={value}
      extensions={extensions}
      basicSetup={{
        lineNumbers: false,
        foldGutter: false,
        highlightActiveLine: false,
      }}
      onChange={(v) => onChange(v)}
    />
  );
}
